import { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { fetchUserByEmail } from "../utils/api";
import { userHasVenue } from "../utils/venueApi";
import type { PersonaId } from "../utils/personas";

interface UserPersona {
  persona: PersonaId | null;
  useCases: string[];
  goals: string[];
  hasVenue: boolean;
  ready: boolean;
}

// Reads onboarding answers from Clerk's unsafeMetadata and probes the venues
// API for whether this user has a venue, so the sidebar can hide irrelevant
// surfaces (e.g. Venue tools for non-hospitality users).
//
// We only hit the venues API when the user's persona suggests they could plausibly
// run a venue (hospitality, or null when onboarding hasn't been answered yet). For
// recruiters/sales/etc the probe is skipped entirely — saves a request and avoids
// the dev-tools 404 noise from a feature they don't use.
const VENUE_RELEVANT_PERSONAS: (PersonaId | null)[] = ["hospitality", null];

export function useUserPersona(): UserPersona {
  const { user, isSignedIn } = useUser();
  const [hasVenue, setHasVenue] = useState(false);
  const [ready, setReady] = useState(false);

  const meta = (user?.unsafeMetadata || {}) as Record<string, unknown>;
  const persona = (meta.persona as PersonaId | undefined) || null;
  const useCases = Array.isArray(meta.useCases) ? (meta.useCases as string[]) : [];
  const goals = Array.isArray(meta.goals) ? (meta.goals as string[]) : [];

  useEffect(() => {
    let cancelled = false;
    async function probe() {
      if (!isSignedIn || !user) {
        if (!cancelled) {
          setHasVenue(false);
          setReady(true);
        }
        return;
      }
      // Skip the probe for personas that have nothing to do with venues.
      if (!VENUE_RELEVANT_PERSONAS.includes(persona)) {
        if (!cancelled) {
          setHasVenue(false);
          setReady(true);
        }
        return;
      }
      const email = user.primaryEmailAddress?.emailAddress;
      if (!email) {
        if (!cancelled) setReady(true);
        return;
      }
      try {
        const dbUser = await fetchUserByEmail(email);
        const has = await userHasVenue(dbUser.id);
        if (!cancelled) setHasVenue(has);
      } catch {
        if (!cancelled) setHasVenue(false);
      } finally {
        if (!cancelled) setReady(true);
      }
    }
    probe();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, user, persona]);

  return { persona, useCases, goals, hasVenue, ready };
}
